// Previsão do tempo do painel inicial — Open-Meteo (previsão) + BigDataCloud
// (nome da cidade / localização aproximada), chamados direto do navegador:
// as duas APIs liberam CORS e não pedem chave. Mesma lógica do SifWeb, com
// os alertas adaptados pro dia a dia do comércio em vez do campo.

export interface ICondicaoTempo {
  texto: string;
  icone: 'sol' | 'nublado' | 'chuva' | 'tempestade' | 'neblina' | 'neve';
}

export interface IPrevisaoAtual {
  temperatura: number;
  umidade: number;
  velocidadeVento: number;
  condicao: ICondicaoTempo;
}

export interface IPrevisaoDiaria {
  data: string;
  tempMax: number;
  tempMin: number;
  probabilidadeChuva: number;
  ventoMax: number;
  condicao: ICondicaoTempo;
}

export type ISeveridadeAlerta = 'perigo' | 'atencao' | 'positivo';

export interface IAlertaTempo {
  severidade: ISeveridadeAlerta;
  titulo: string;
  mensagem: string;
}

export interface IPrevisaoTempo {
  cidade: string;
  // Coordenadas usadas na previsão — também abrem a previsão completa no
  // site de clima (ver linkPrevisaoCompleta).
  latitude: number;
  longitude: number;
  atual: IPrevisaoAtual;
  diaria: IPrevisaoDiaria[];
  alertas: IAlertaTempo[];
  // 'ip' = localização aproximada pela conexão (quando o navegador não libera o GPS).
  origemLocalizacao: 'navegador' | 'manual' | 'ip';
}

interface ILocalizacao {
  latitude: number;
  longitude: number;
  nomeExibicao: string;
}

const CHAVE_LOCALIZACAO_MANUAL = 'APP_LOCALIZACAO_MANUAL';

const condicaoPorCodigo = (codigo: number): ICondicaoTempo => {
  if (codigo === 0) return { texto: 'Céu limpo', icone: 'sol' };
  if ([1, 2].includes(codigo)) return { texto: 'Poucas nuvens', icone: 'sol' };
  if (codigo === 3) return { texto: 'Nublado', icone: 'nublado' };
  if ([45, 48].includes(codigo)) return { texto: 'Neblina', icone: 'neblina' };
  if ([51, 53, 55, 56, 57].includes(codigo)) return { texto: 'Garoa', icone: 'chuva' };
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(codigo)) return { texto: 'Chuva', icone: 'chuva' };
  if ([71, 73, 75, 77, 85, 86].includes(codigo)) return { texto: 'Neve', icone: 'neve' };
  if ([95, 96, 99].includes(codigo)) return { texto: 'Trovoada', icone: 'tempestade' };
  return { texto: 'Indefinido', icone: 'nublado' };
};

const obterCoordenadas = (): Promise<GeolocationCoordinates> => new Promise((resolve, reject) => {
  // Fora de HTTPS o navegador bloqueia a geolocalização sem nem perguntar —
  // falha na hora em vez de esperar o timeout.
  if (!window.isSecureContext) {
    reject(new Error('Localização do navegador indisponível neste endereço (sem HTTPS).'));
    return;
  }
  if (!navigator.geolocation) {
    reject(new Error('Este navegador não suporta geolocalização.'));
    return;
  }
  navigator.geolocation.getCurrentPosition(
    posicao => resolve(posicao.coords),
    erro => reject(new Error(erro.code === erro.PERMISSION_DENIED
      ? 'Permissão de localização negada. Ative a localização ou digite sua cidade.'
      : 'Não foi possível obter sua localização.')),
    { enableHighAccuracy: false, timeout: 10000, maximumAge: 15 * 60 * 1000 },
  );
});

const salvarLocalizacaoManual = (localizacao: ILocalizacao) => {
  try {
    localStorage.setItem(CHAVE_LOCALIZACAO_MANUAL, JSON.stringify(localizacao));
  } catch {
    // localStorage indisponível; a cidade só não fica salva pra próxima vez.
  }
};

const obterLocalizacaoManualSalva = (): ILocalizacao | null => {
  try {
    const salvo = localStorage.getItem(CHAVE_LOCALIZACAO_MANUAL);
    return salvo ? JSON.parse(salvo) : null;
  } catch {
    return null;
  }
};

const buscarCoordenadasPorCidade = async (nomeCidade: string): Promise<ILocalizacao> => {
  // A geocodificação não reconhece "Cidade - UF"; só o nome da cidade basta.
  const termoBusca = nomeCidade.split(/\s+-\s+/)[0].trim();
  const resposta = await fetch(
    `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(termoBusca)}&count=1&language=pt&format=json`,
  );
  const dados = await resposta.json();
  const resultado = dados.results?.[0];
  if (!resultado) throw new Error('Cidade não encontrada. Tente digitar de outra forma (ex: "Cidade - UF").');
  const nomeExibicao = resultado.admin1 ? `${resultado.name} - ${resultado.admin1}` : resultado.name;
  return { latitude: resultado.latitude, longitude: resultado.longitude, nomeExibicao };
};

const nomeDaCidade = (dados: { city?: string; locality?: string; principalSubdivisionCode?: string }) => {
  const cidade = dados.city || dados.locality;
  const estado = dados.principalSubdivisionCode?.replace('BR-', '');
  if (cidade && estado) return `${cidade} - ${estado}`;
  return cidade || 'Sua localização';
};

const obterCidade = async (latitude: number, longitude: number): Promise<string> => {
  try {
    const resposta = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=pt`,
    );
    return nomeDaCidade(await resposta.json());
  } catch {
    return 'Sua localização';
  }
};

// Sem latitude/longitude, o mesmo serviço responde com a posição estimada
// pela conexão.
const obterLocalizacaoPorIp = async (): Promise<ILocalizacao> => {
  const resposta = await fetch('https://api.bigdatacloud.net/data/reverse-geocode-client?localityLanguage=pt');
  const dados = await resposta.json();
  if (typeof dados.latitude !== 'number' || typeof dados.longitude !== 'number') {
    throw new Error('Não foi possível identificar sua localização automaticamente.');
  }
  return { latitude: dados.latitude, longitude: dados.longitude, nomeExibicao: nomeDaCidade(dados) };
};

// Regras que ligam a previsão ao dia a dia da loja/empresa: chuva e
// temporal afetam entregas e o movimento de clientes, calor pede cuidado com
// perecíveis, e dias firmes são bons pra rotas e ações externas.
const gerarAlertas = (diaria: IPrevisaoDiaria[]): IAlertaTempo[] => {
  const alertas: IAlertaTempo[] = [];
  const [hoje, amanha] = diaria;

  const temporal = [hoje, amanha].find(dia => dia && dia.condicao.icone === 'tempestade');
  if (temporal) {
    alertas.push({
      severidade: 'perigo',
      titulo: temporal === hoje ? 'Temporal previsto para hoje' : 'Temporal previsto para amanhã',
      mensagem: 'Risco de atrasos em entregas e coletas. Confirme rotas e prazos com transportadoras e clientes.',
    });
  }

  if (hoje && hoje.probabilidadeChuva >= 60) {
    alertas.push({
      severidade: 'atencao',
      titulo: 'Chuva prevista para hoje',
      mensagem: `${hoje.probabilidadeChuva}% de chance de chuva. Entregas podem atrasar e o movimento na loja tende a cair.`,
    });
  } else if (amanha && amanha.probabilidadeChuva >= 60) {
    alertas.push({
      severidade: 'atencao',
      titulo: 'Chuva prevista para amanhã',
      mensagem: `${amanha.probabilidadeChuva}% de chance de chuva amanhã. Se puder, antecipe entregas e rotas para hoje.`,
    });
  }

  const diaDeCalor = [hoje, amanha].find(dia => dia && dia.tempMax >= 35);
  if (diaDeCalor) {
    alertas.push({
      severidade: 'atencao',
      titulo: 'Calor intenso',
      mensagem: `Máxima de ${Math.round(diaDeCalor.tempMax)}°C. Atenção ao armazenamento e transporte de produtos perecíveis.`,
    });
  }

  const diaDeFrio = [hoje, amanha].find(dia => dia && dia.tempMin <= 5);
  if (diaDeFrio) {
    alertas.push({
      severidade: 'atencao',
      titulo: 'Frio intenso',
      mensagem: `Mínima de ${Math.round(diaDeFrio.tempMin)}°C prevista. Boa hora para destacar produtos de inverno.`,
    });
  }

  const proximosTresDias = diaria.slice(0, 3);
  if (proximosTresDias.length === 3 && proximosTresDias.every(dia => dia.probabilidadeChuva < 30)) {
    alertas.push({
      severidade: 'positivo',
      titulo: 'Tempo firme nos próximos dias',
      mensagem: 'Sem previsão de chuva relevante em 3 dias — bom momento para entregas, rotas e ações externas.',
    });
  }

  return alertas;
};

const montarPrevisao = async (
  latitude: number,
  longitude: number,
  cidadeConhecida: string | undefined,
  origemLocalizacao: IPrevisaoTempo['origemLocalizacao'],
): Promise<IPrevisaoTempo> => {
  const [clima, cidade] = await Promise.all([
    fetch(
      'https://api.open-meteo.com/v1/forecast'
      + `?latitude=${latitude}&longitude=${longitude}`
      + '&current_weather=true'
      + '&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,windspeed_10m_max,weathercode'
      + '&hourly=relativehumidity_2m'
      + '&timezone=auto&forecast_days=4',
    ).then(resposta => resposta.json()),
    cidadeConhecida ? Promise.resolve(cidadeConhecida) : obterCidade(latitude, longitude),
  ]);

  const diaria: IPrevisaoDiaria[] = clima.daily.time.map((data: string, indice: number) => ({
    data,
    tempMax: clima.daily.temperature_2m_max[indice],
    tempMin: clima.daily.temperature_2m_min[indice],
    probabilidadeChuva: clima.daily.precipitation_probability_max[indice] ?? 0,
    ventoMax: clima.daily.windspeed_10m_max[indice],
    condicao: condicaoPorCodigo(clima.daily.weathercode[indice]),
  }));

  const horaAtualIndice = Math.max(0, clima.hourly.time.indexOf(`${clima.current_weather.time.slice(0, 13)}:00`));

  return {
    cidade,
    latitude,
    longitude,
    atual: {
      temperatura: clima.current_weather.temperature,
      velocidadeVento: clima.current_weather.windspeed,
      umidade: clima.hourly.relativehumidity_2m[horaAtualIndice] ?? 0,
      condicao: condicaoPorCodigo(clima.current_weather.weathercode),
    },
    diaria,
    alertas: gerarAlertas(diaria),
    origemLocalizacao,
  };
};

// Ordem: GPS do navegador (preciso) → cidade digitada antes pelo usuário →
// localização aproximada pela conexão. Só falha se nenhuma funcionar.
const obterPrevisaoTempo = async (): Promise<IPrevisaoTempo> => {
  try {
    const coords = await obterCoordenadas();
    return await montarPrevisao(coords.latitude, coords.longitude, undefined, 'navegador');
  } catch (erroGeolocalizacao) {
    const localizacaoManual = obterLocalizacaoManualSalva();
    if (localizacaoManual) {
      return montarPrevisao(localizacaoManual.latitude, localizacaoManual.longitude, localizacaoManual.nomeExibicao, 'manual');
    }
    try {
      const porIp = await obterLocalizacaoPorIp();
      return await montarPrevisao(porIp.latitude, porIp.longitude, porIp.nomeExibicao, 'ip');
    } catch {
      throw erroGeolocalizacao;
    }
  }
};

const definirLocalizacaoManual = async (nomeCidade: string): Promise<IPrevisaoTempo> => {
  const localizacao = await buscarCoordenadasPorCidade(nomeCidade);
  salvarLocalizacaoManual(localizacao);
  return montarPrevisao(localizacao.latitude, localizacao.longitude, localizacao.nomeExibicao, 'manual');
};

// ---- Previsão detalhada (dialog do painel) --------------------------------

export interface IPrevisaoHora {
  hora: string; // ISO local, ex.: 2026-10-15T14:00
  temperatura: number;
  probabilidadeChuva: number;
  condicao: ICondicaoTempo;
}

export interface IPrevisaoDiaDetalhada extends IPrevisaoDiaria {
  sensacaoMax: number;
  sensacaoMin: number;
  chuvaMm: number;
  rajadaMax: number;
  uvMax: number;
  nascerSol: string; // ISO local
  porSol: string;
}

export interface IPrevisaoDetalhada {
  atual: IPrevisaoAtual & {
    sensacao: number;
    direcaoVento: number; // graus
    rajada: number;
    pressao: number; // hPa
    nuvens: number; // %
    chuvaMm: number;
  };
  horas: IPrevisaoHora[]; // próximas 24 horas
  dias: IPrevisaoDiaDetalhada[]; // 7 dias
}

// Busca à parte (só quando o dialog abre), pra não deixar o painel inicial
// mais pesado: mesmas coordenadas da previsão resumida.
const obterPrevisaoDetalhada = async (latitude: number, longitude: number): Promise<IPrevisaoDetalhada> => {
  const resposta = await fetch(
    'https://api.open-meteo.com/v1/forecast'
    + `?latitude=${latitude}&longitude=${longitude}`
    + '&current=temperature_2m,apparent_temperature,relative_humidity_2m,weather_code,wind_speed_10m,wind_direction_10m,wind_gusts_10m,pressure_msl,cloud_cover,precipitation'
    + '&hourly=temperature_2m,precipitation_probability,weather_code'
    + '&daily=weather_code,temperature_2m_max,temperature_2m_min,apparent_temperature_max,apparent_temperature_min,sunrise,sunset,uv_index_max,precipitation_sum,precipitation_probability_max,wind_speed_10m_max,wind_gusts_10m_max'
    + '&timezone=auto&forecast_days=7',
  );
  if (!resposta.ok) throw new Error('Não foi possível carregar a previsão detalhada.');
  const clima = await resposta.json();

  const horaAtual = `${String(clima.current.time).slice(0, 13)}:00`;
  const inicio = Math.max(0, clima.hourly.time.indexOf(horaAtual));
  const horas: IPrevisaoHora[] = clima.hourly.time.slice(inicio, inicio + 24).map((hora: string, i: number) => ({
    hora,
    temperatura: clima.hourly.temperature_2m[inicio + i],
    probabilidadeChuva: clima.hourly.precipitation_probability[inicio + i] ?? 0,
    condicao: condicaoPorCodigo(clima.hourly.weather_code[inicio + i]),
  }));

  const dias: IPrevisaoDiaDetalhada[] = clima.daily.time.map((data: string, i: number) => ({
    data,
    tempMax: clima.daily.temperature_2m_max[i],
    tempMin: clima.daily.temperature_2m_min[i],
    probabilidadeChuva: clima.daily.precipitation_probability_max[i] ?? 0,
    ventoMax: clima.daily.wind_speed_10m_max[i],
    condicao: condicaoPorCodigo(clima.daily.weather_code[i]),
    sensacaoMax: clima.daily.apparent_temperature_max[i],
    sensacaoMin: clima.daily.apparent_temperature_min[i],
    chuvaMm: clima.daily.precipitation_sum[i] ?? 0,
    rajadaMax: clima.daily.wind_gusts_10m_max[i] ?? 0,
    uvMax: clima.daily.uv_index_max[i] ?? 0,
    nascerSol: clima.daily.sunrise[i],
    porSol: clima.daily.sunset[i],
  }));

  return {
    atual: {
      temperatura: clima.current.temperature_2m,
      sensacao: clima.current.apparent_temperature,
      umidade: clima.current.relative_humidity_2m,
      velocidadeVento: clima.current.wind_speed_10m,
      direcaoVento: clima.current.wind_direction_10m,
      rajada: clima.current.wind_gusts_10m,
      pressao: clima.current.pressure_msl,
      nuvens: clima.current.cloud_cover,
      chuvaMm: clima.current.precipitation ?? 0,
      condicao: condicaoPorCodigo(clima.current.weather_code),
    },
    horas,
    dias,
  };
};

// Previsão completa (hora a hora, 10 dias, radar) no weather.com em
// português, já posicionada nas coordenadas do usuário.
const linkPrevisaoCompleta = (previsao: IPrevisaoTempo) =>
  `https://weather.com/pt-BR/clima/hoje/l/${previsao.latitude.toFixed(4)},${previsao.longitude.toFixed(4)}`;

export const PrevisaoTempoService = {
  obterPrevisaoTempo,
  definirLocalizacaoManual,
  obterPrevisaoDetalhada,
  linkPrevisaoCompleta,
};
